import { FC, useState } from 'react';
import clsx from 'clsx';

import Icon from '@components/icon/Icon';
import { inputStyles } from '@components/input/input.config';
import Label from '@components/label/Label';
import testId from '@constants/testId';

import TimePickerPopup from './TimePickerPopup';

interface TimePickerProps {
  label: string;
  time: string;
  handleChange: (time: string) => void;
  isOutsideClickRequired?: boolean;
  isRequired?: boolean;
  labelStyle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  inputStyle?: string;
  popupClassName?: string;
  iconPosition?: 'left' | 'right';
  pickerInputStyles?: string;
  defaultToCurrentTime?: boolean;
}

const TimePicker: FC<TimePickerProps> = ({
  label,
  time,
  handleChange,
  isOutsideClickRequired = false,
  isRequired = false,
  labelStyle = '',
  disabled = false,
  error,
  className,
  inputStyle,
  popupClassName,
  iconPosition = 'right',
  pickerInputStyles,
  defaultToCurrentTime = false
}) => {
  const [showTimePicker, setShowTimePicker] = useState(false);

  const toggleTimePicker = () => setShowTimePicker((prev) => !prev);

  const handleConfirm = (hour: string, minute: string, ampm: string) => {
    let updatedHour = hour;
    if (hour.length === 1) updatedHour = `0${hour}`;

    let updatedMinute = minute;
    if (minute.length === 1) updatedMinute = `0${minute}`;

    const updatedTime = `${updatedHour}:${updatedMinute} ${ampm}`;
    handleChange(updatedTime);
    toggleTimePicker();
  };

  const onCancel = () => {
    toggleTimePicker();
  };

  return (
    <div className={`relative ${className || ''}`}>
      <div className="space-y-2">
        <Label label={label} className={labelStyle} isRequired={isRequired} />
        <div
          className={`${
            inputStyles.common
          } ${inputStyle} flex h-12.5 items-center justify-between ${pickerInputStyles} ${
            disabled ? 'cursor-not-allowed' : ''
          }`}
          role="presentation"
          onClick={disabled ? undefined : toggleTimePicker}
        >
          <div className="flex items-center gap-2 text-grey">
            {iconPosition === 'left' && <Icon name="clock" />}
            {time ? `${time}` : '--:--'}
          </div>
          {iconPosition === 'right' && <Icon name="clock" />}
        </div>
        {error && (
          <div
            className={clsx('my-2 flex items-center text-xs text-error')}
            data-testid={testId.input.input_error}
          >
            {error}
          </div>
        )}
      </div>

      {showTimePicker && (
        <TimePickerPopup
          handleConfirm={handleConfirm}
          onCancel={onCancel}
          isOutsideClickRequired={isOutsideClickRequired}
          time={time}
          className={popupClassName}
          defaultToCurrentTime={defaultToCurrentTime}
        />
      )}
    </div>
  );
};
export default TimePicker;
